import log from 'embed_v2/helpers/log'
import { startTimerFn, formatTime } from 'embed_v2/helpers/timer'
import { useState, useEffect, useContext } from 'preact/hooks'
import PropTypes from 'prop-types'
import { formatSeconds } from 'core/helpers/formatters'
import { getStylesFn } from 'core/helpers/styles'
import i18n from 'core/services/i18n'
import { ElementTimerOptions } from 'core/types/element/element_timer'
import optionsPropTypes from 'PropsSpec'
import WidgetContext from '../Context'
import TimerBox from './timer/TimerBox'

const extractStyles = getStylesFn(
  [
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
    'borderTop',
    'borderRight',
    'borderBottom',
    'borderLeft',
    'borderTopLeftRadius',
    'borderTopRightRadius',
    'borderBottomRightRadius',
    'borderBottomLeftRadius',
    'backgroundColor'
  ],
  {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-around',
    textAlign: 'center'
  }
)

const defaultRemain = { days: '00', hours: '00', minutes: '00', seconds: '00', totalSeconds: 0 }

type Props = {
  options: ElementTimerOptions
}

const Timer = (props: Props) => {
  const { options } = props
  const { session, remainTimeWorker } = useContext(WidgetContext)

  const [remain, setRemain] = useState(defaultRemain)

  useEffect(() => {
    if (options.type === 'sync') return

    switch (options.type) {
      case 'evergreen': {
        if (options.evergreen_value === 0) return

        const runTimer = () =>
          startTimerFn(options.evergreen_value, val => {
            if (val.totalSeconds === 0) {
              runTimer()
            } else {
              setRemain(val)
            }
          })

        return runTimer()
      }
      case 'fixed': {
        const t = formatTime(options.fixed_value)
        if (t) {
          return startTimerFn(t, setRemain)
        }

        break
      }
      default:
        break
    }
  }, [options.type])

  useEffect(() => {
    if (options.type !== 'sync') return

    if (!session?.scheduled_at) {
      setRemain(defaultRemain)
      return
    }

    const t = formatTime(session.scheduled_at)

    if (session && t) setRemain(formatSeconds(t))
    if (!remainTimeWorker) return

    remainTimeWorker.addEventListener('message', e => {
      log.debug('[Timer] onMessage', e.data)
      setRemain(formatSeconds(e.data))
    })
  }, [options.type, session, remainTimeWorker])

  const styles = extractStyles(options)

  if (options.type === 'sync' && !session?.scheduled_at) return null

  return (
    <div style={styles} className="wf_reset">
      <TimerBox options={options} label={i18n.t('layout.time.days')} value={remain.days} />
      <TimerBox options={options} label={i18n.t('layout.time.hours')} value={remain.hours} />
      <TimerBox options={options} label={i18n.t('layout.time.mins')} value={remain.minutes} />
      <TimerBox options={options} label={i18n.t('layout.time.secs')} value={remain.seconds} />
    </div>
  )
}

Timer.propTypes = {
  scheduled_at: PropTypes.string,
  options: optionsPropTypes('timer')
}

export default Timer
